import { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Localised holiday-closure banner texts.
// Shows automatically (1) before the closure starts (14-day lead) and (2) during the closure.
const COPY = {
  de: {
    pre: (s, e) => `BETRIEBSFERIEN · ${formatDate(s, "de")} – ${formatDate(e, "de")}`,
    during: (e) => `Wir haben Betriebsferien. Ab ${nextDay(e, "de")} sind wir wieder für Sie da.`,
    detail: (s, e) => `Vom ${formatDate(s, "de")} bis ${formatDate(e, "de")} haben wir Betriebsferien. Ab ${nextDay(e, "de")} sind wir wieder normal geöffnet.`,
  },
  en: {
    pre: (s, e) => `COMPANY HOLIDAYS · ${formatDate(s, "en")} – ${formatDate(e, "en")}`,
    during: (e) => `Closed for company holidays. We reopen ${nextDay(e, "en")}.`,
    detail: (s, e) => `From ${formatDate(s, "en")} to ${formatDate(e, "en")} we are on company holidays. We reopen on ${nextDay(e, "en")}.`,
  },
  fr: {
    pre: (s, e) => `VACANCES ANNUELLES · ${formatDate(s, "fr")} – ${formatDate(e, "fr")}`,
    during: (e) => `Nous sommes en vacances annuelles. Réouverture le ${nextDay(e, "fr")}.`,
    detail: (s, e) => `Du ${formatDate(s, "fr")} au ${formatDate(e, "fr")} nous sommes en vacances annuelles. Réouverture le ${nextDay(e, "fr")}.`,
  },
  it: {
    pre: (s, e) => `FERIE AZIENDALI · ${formatDate(s, "it")} – ${formatDate(e, "it")}`,
    during: (e) => `Siamo in ferie aziendali. Riapertura il ${nextDay(e, "it")}.`,
    detail: (s, e) => `Dal ${formatDate(s, "it")} al ${formatDate(e, "it")} siamo in ferie aziendali. Riapertura il ${nextDay(e, "it")}.`,
  },
  zh: {
    pre: (s, e) => `公司休假 · ${formatDate(s, "zh")} – ${formatDate(e, "zh")}`,
    during: (e) => `本店休假中，${nextDay(e, "zh")} 恢復正常營業。`,
    detail: (s, e) => `本店於 ${formatDate(s, "zh")} 至 ${formatDate(e, "zh")} 公司休假，${nextDay(e, "zh")} 恢復正常營業。`,
  },
};

function formatDate(iso, lang) {
  const d = new Date(iso);
  if (lang === "zh") return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  const localeMap = { de: "de-DE", en: "en-GB", fr: "fr-FR", it: "it-IT" };
  return d.toLocaleDateString(localeMap[lang] || "de-DE", { day: "numeric", month: "long", year: "numeric" });
}

function nextDay(endIso, lang) {
  const d = new Date(endIso);
  d.setDate(d.getDate() + 1);
  return formatDate(d.toISOString().slice(0, 10), lang);
}

export default function HolidayBanner() {
  const { lang } = useLanguage();
  const [closure, setClosure] = useState(null);

  useEffect(() => {
    axios.get(`${API}/opening-hours`).then(({ data }) => {
      if (data.holiday_closure) setClosure(data.holiday_closure);
    }).catch(() => {});
  }, []);

  if (!closure) return null;
  const copy = COPY[lang] || COPY.de;

  return (
    <div
      className="w-full bg-gradient-to-r from-[var(--lacquer-deep)] via-[var(--lacquer)] to-[var(--lacquer-deep)] border-b border-[var(--gold)]/40 fixed top-0 left-0 right-0 z-[60] shadow-lg"
      data-testid="holiday-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 bg-[var(--accent-fg-bright)] rounded-full animate-pulse" />
          <span className="text-[15px] sm:text-[12px] uppercase tracking-[0.3em] text-[var(--accent-fg-bright)] font-bold whitespace-nowrap">
            {copy.pre(closure.start, closure.end)}
          </span>
        </span>
        <span className="hidden sm:inline-block w-px h-4 bg-[var(--accent-fg-bright)]/40" />
        <span className="text-[12px] sm:text-[13px] text-[var(--accent-fg)] leading-tight" style={{ fontFamily: "var(--serif)" }}>
          {closure.active_now ? copy.during(closure.end) : copy.detail(closure.start, closure.end)}
        </span>
      </div>
    </div>
  );
}
